import { CreditCardIcon, LandmarkIcon } from "lucide-react";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { cn } from "@/utils/cn";

import type { PaymentMethod, PaymentMethodSlot } from "../types";

import { CardBrandMark } from "../../CardBrandMark";
import {
  paymentMethodStatus,
  type PaymentMethodStatus,
} from "../utils/card-expiry";
import {
  maskedNumber,
  paymentMethodDetails,
  slotDescription,
  slotLabel,
} from "../utils/payment-method-format";

interface PaymentMethodCardProps {
  readonly method: PaymentMethod;
  readonly onUpdate: () => void;
  readonly slot: PaymentMethodSlot;
}

export function PaymentMethodCard({
  method,
  onUpdate,
  slot,
}: PaymentMethodCardProps) {
  const TypeIcon = method.type === "card" ? CreditCardIcon : LandmarkIcon;
  const status = paymentMethodStatus(method);
  const isHighlighted = slot.kind === "default" && status === "ok";

  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col justify-center gap-md rounded-md border p-3xl md:min-h-53",
        status === "ok" &&
          (isHighlighted
            ? "border-border-brand-solid bg-bg-brand-primary"
            : "border-border-primary bg-bg-primary"),
        status === "expiring" &&
          "border-fg-warning-secondary bg-utility-warning-50",
        status === "expired" && "border-border-error bg-utility-error-50",
      )}
    >
      <div className="flex flex-col gap-md pb-md">
        <div className="flex items-start justify-between gap-md">
          <div className="flex min-w-0 items-center gap-md">
            <span
              className={cn(
                "text-xs uppercase",
                isHighlighted
                  ? "font-semibold text-text-brand-primary"
                  : "font-medium text-text-quaternary",
              )}
            >
              {slotLabel(slot)}
            </span>
            <StatusBadge status={status} />
          </div>
          <TypeIcon
            aria-hidden="true"
            className={cn(
              "shrink-0",
              isHighlighted ? "text-fg-brand-primary" : "text-fg-quaternary",
            )}
            size={20}
          />
        </div>

        <div
          className={cn(
            "flex items-center",
            method.type === "card" ? "gap-xl" : "gap-lg",
          )}
        >
          {method.type === "card" ? (
            <CardBrandMark brand={method.brand} size="lg" />
          ) : (
            <LandmarkIcon
              aria-hidden="true"
              className="shrink-0 text-text-primary"
              size={32}
            />
          )}
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-md font-medium text-text-primary">
              {maskedNumber(method)}
            </span>
            <span className="text-xs text-text-secondary">
              {paymentMethodDetails(method)}
            </span>
          </div>
        </div>
      </div>

      <div className="h-px bg-border-secondary" />

      <div className="flex items-center justify-between gap-md pt-md">
        <span className="text-sm text-text-quaternary">
          {slotDescription(slot)}
        </span>
        <Button
          className="p-0 hover:bg-transparent"
          color="color"
          onPress={onUpdate}
          size="sm"
          variant="tertiary"
        >
          Update
        </Button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: PaymentMethodStatus }) {
  if (status === "ok") return null;

  return (
    <Badge
      isRounded
      size="small"
      variant={status === "expired" ? "error" : "warning"}
    >
      {status === "expired" ? "Expired" : "Expires soon"}
    </Badge>
  );
}
